import { ComponentProps, useEffect, useState } from "react";

type TypingIdsProps = {
  ids: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  delayBetweenIds?: number;
};

export default function TypingIds({
  ids,
  typingSpeed = 100,
  deletingSpeed = 50,
  delayBetweenIds = 1500,
  ...props
}: TypingIdsProps & Omit<ComponentProps<"span">, keyof TypingIdsProps>) {
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentIdIndex, setCurrentIdIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);

  const index = currentIdIndex % ids.length;
  const currentText = ids[index];

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isDeleting && charIndex < currentText.length) {
      timeout = setTimeout(() => {
        setDisplayedText(currentText.slice(0, charIndex + 1));
        setCharIndex((prev) => prev + 1);
      }, typingSpeed);
    } else if (!isDeleting && charIndex === currentText.length) {
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, delayBetweenIds);
    } else if (isDeleting && charIndex > 0) {
      timeout = setTimeout(() => {
        setDisplayedText(currentText.slice(0, charIndex - 1));
        setCharIndex((prev) => prev - 1);
      }, deletingSpeed);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setCurrentIdIndex((prev) => prev + 1);
    }

    return () => clearTimeout(timeout);
  }, [
    charIndex,
    isDeleting,
    currentText,
    typingSpeed,
    deletingSpeed,
    delayBetweenIds,
  ]);

  return (
    <span {...props}>
      {displayedText}
      <span className="animate-pulse">|</span>
    </span>
  );
}
